import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Input, Checkbox, Button, Spin, Tag, Select, InputNumber,
  Tooltip, message, Empty, Typography, Popover, Modal, AutoComplete,
} from 'antd';
import {
  PlayCircleOutlined, DatabaseOutlined, GlobalOutlined,
  SearchOutlined, CopyOutlined, ClearOutlined,
  BranchesOutlined, TableOutlined, CodeOutlined,
  ThunderboltOutlined, FilterOutlined,
  KeyOutlined, DeleteOutlined, ReloadOutlined,
  EyeOutlined, EyeInvisibleOutlined,
  RightOutlined, DownOutlined, LockOutlined, LeftOutlined,
} from '@ant-design/icons';
import {
  getGraphQLSchema, getCatalogViews, executeGraphQLQuery,
  getGraphQLApiKey, createGraphQLApiKey, deleteGraphQLApiKey,
} from '../services/dataService';
import { useAuth } from '../contexts/AuthContext';
import './GraphQLBuilder.css';

const { Text } = Typography;

// ─── Helpers ───────────────────────────────────────────────────────────

/** Parse schema name into path segments: strip REFINED__ prefix, split on __. */
function parseSchemaSegments(schema) {
  let name = schema;
  if (name.startsWith('REFINED__')) name = name.substring(9);
  return name.split('__').filter(Boolean);
}

/**
 * Build a schema tree from views.
 * Each node: { name, fullPath, tables: [...], children: { name: node } }
 * Schemas are split by __ segments to form the hierarchy.
 */
function buildSchemaTree(views) {
  const root = { name: 'ROOT', fullPath: '', tables: [], children: {} };

  Object.values(views).forEach(v => {
    let schemaRaw = 'OTHER';
    if (v.qualified) {
      const dotIdx = v.qualified.indexOf('.');
      if (dotIdx > 0) schemaRaw = v.qualified.substring(0, dotIdx);
    }
    const segments = parseSchemaSegments(schemaRaw);
    let node = root;
    segments.forEach((seg, i) => {
      if (!node.children[seg]) {
        node.children[seg] = {
          name: seg,
          fullPath: segments.slice(0, i + 1).join('__'),
          tables: [],
          children: {},
        };
      }
      node = node.children[seg];
    });
    node.tables.push(v);
  });

  // Sort tables and children within each node
  function sortNode(node) {
    node.tables.sort((a, b) => a.label.localeCompare(b.label));
    // Convert children to sorted array
    node.childrenArr = Object.values(node.children)
      .sort((a, b) => a.name.localeCompare(b.name));
    node.childrenArr.forEach(sortNode);
  }
  sortNode(root);
  return root;
}

/** Check if a tree node or its descendants match a search string. */
function treeMatchesSearch(node, lower) {
  if (node.name.toLowerCase().includes(lower)) return true;
  if (node.tables.some(v => v.label.toLowerCase().includes(lower) || v.id.toLowerCase().includes(lower))) return true;
  return (node.childrenArr || []).some(child => treeMatchesSearch(child, lower));
}

/** Filter tree nodes to only include those matching search. */
function filterTree(node, lower) {
  const nameMatches = node.name.toLowerCase().includes(lower);
  const filteredTables = nameMatches
    ? node.tables
    : node.tables.filter(v => v.label.toLowerCase().includes(lower) || v.id.toLowerCase().includes(lower));
  const filteredChildren = (node.childrenArr || [])
    .filter(child => nameMatches || treeMatchesSearch(child, lower))
    .map(child => nameMatches ? child : filterTree(child, lower));
  return { ...node, tables: filteredTables, childrenArr: filteredChildren };
}

/** Nest tables hierarchically: tables whose label starts with another table's label + '_' become children. */
function nestTables(tables) {
  const sorted = [...tables].sort((a, b) => a.label.localeCompare(b.label));
  const roots = [];
  const labelMap = {};
  for (const t of sorted) {
    const node = { ...t, childTables: [] };
    let parent = null;
    const parts = t.label.split('_');
    for (let i = parts.length - 1; i > 0; i--) {
      const prefix = parts.slice(0, i).join('_');
      if (labelMap[prefix]) { parent = labelMap[prefix]; break; }
    }
    if (parent) parent.childTables.push(node);
    else roots.push(node);
    labelMap[t.label] = node;
  }
  return roots;
}

/** Get Snowflake qualified name for a view (SCHEMA.VIEW_NAME). */
function getQualifiedName(views, nodeId) {
  const view = views[nodeId];
  if (!view) return nodeId;
  // Use the actual qualified name from catalog if available
  if (view.qualified) return view.qualified;
  // Fallback: nodeId.label (should not happen with catalog enrichment)
  return `${nodeId}.${view.label}`;
}

/** Build a unique alias path key for a node in the expansion tree. */
function makePathKey(parentPath, nodeId, joinColumn) {
  return parentPath ? `${parentPath}>${nodeId}:${joinColumn}` : nodeId;
}

/** Build the nest segment name for a relationship.
 *  Forward:  relationship_<TARGET>   e.g. "is_owned_by_TEAM"
 *  Reverse:  <TARGET>_relationship_<PARENT>  e.g. "CLOUD_SUBSCRIPTION_is_owned_by_TEAM"
 */
function nestSegmentName(rel, parentLabel, targetLabel) {
  const relType = rel.relationship || '';
  if (rel.reverse) {
    return relType ? `${targetLabel}_${relType}_${parentLabel}` : targetLabel;
  }
  return relType ? `${relType}_${targetLabel}` : targetLabel;
}

/**
 * Recursively build JOINs and SELECT columns from the expansion tree.
 * Also builds columnMapping: { sqlAlias: { nestPath: string[], colName: string } }
 * so the response can be nested into GraphQL-style JSON.
 */
function collectJoinsRecursive(
  parentPath, parentAlias, nodeId, expandedMap, selectedFieldsMap,
  views, relationships, aliasCounter, result, nestPath
) {
  const expanded = expandedMap[parentPath] || [];
  const parentView = views[nodeId];
  const parentLabel = parentView ? parentView.label : nodeId;
  for (const rel of expanded) {
    const targetView = views[rel.target];
    if (!targetView) continue;
    const targetQualified = getQualifiedName(views, rel.target);
    const alias = `t${aliasCounter.val++}`;
    const childPath = makePathKey(parentPath, rel.target, rel.joinColumn);
    const segName = nestSegmentName(rel, parentLabel, targetView.label);
    const childNestPath = [...nestPath, segName];
    result.aliasMap[childPath] = alias;

    // Determine the correct join column on the target side
    const targetCol = rel.foreignKey || rel.joinColumn;
    result.joins.push(
      `LEFT JOIN ${targetQualified} ${alias} ON ${parentAlias}."${rel.joinColumn}" = ${alias}."${targetCol}"`
    );

    // Collect selected columns + build column mapping
    const targetFields = selectedFieldsMap[childPath] || [];
    if (targetFields.length > 0) {
      targetFields.forEach(col => {
        const sqlAlias = `${segName}_${col}`;
        result.selectCols.push(`${alias}."${col}" AS "${sqlAlias}"`);
        result.columnMapping[sqlAlias] = { nestPath: childNestPath, colName: col };
      });
    }

    // Recurse into children of this expanded node
    collectJoinsRecursive(
      childPath, alias, rel.target, expandedMap, selectedFieldsMap,
      views, relationships, aliasCounter, result, childNestPath
    );
  }
}

/** Build SQL from the recursive query tree.
 *  Returns { sql, columnMapping } where columnMapping maps SQL column aliases
 *  to their nesting path for GraphQL-style response formatting.
 */
function buildSQL(rootTable, selectedFields, expandedRelations, filters, queryMode, views, relationships, columnsCache) {
  if (!rootTable) return { sql: '', columnMapping: {} };
  const rootView = views[rootTable];
  if (!rootView) return { sql: '', columnMapping: {} };
  const rootQualified = getQualifiedName(views, rootTable);
  const rootPath = rootTable;

  // Root columns
  const rootCols = (selectedFields[rootPath] || []).map(col => `t0."${col}"`);
  if (rootCols.length === 0) rootCols.push('t0.*');

  // Recursive JOIN collection (with column mapping for nesting)
  const result = { selectCols: [], joins: [], aliasMap: { [rootPath]: 't0' }, columnMapping: {} };
  const aliasCounter = { val: 1 };
  collectJoinsRecursive(
    rootPath, 't0', rootTable, expandedRelations, selectedFields,
    views, relationships, aliasCounter, result, []
  );

  const allCols = [...rootCols, ...result.selectCols];
  let sql = `SELECT ${allCols.join(',\n       ')}\nFROM ${rootQualified} t0`;
  if (result.joins.length > 0) sql += '\n' + result.joins.join('\n');

  // WHERE
  // Allowlist of valid SQL operators — prevents SQL injection via f.operator.
  const ALLOWED_SQL_OPERATORS = new Set([
    '=', '!=', '<>', '<', '<=', '>', '>=',
    'IN', 'NOT IN', 'LIKE', 'ILIKE', 'NOT LIKE', 'NOT ILIKE',
    'IS NULL', 'IS NOT NULL', 'BETWEEN',
  ]);
  const whereClauses = [];
  (filters || []).forEach(f => {
    if (f.column && f.operator && f.value !== undefined && f.value !== '') {
      // Reject any operator not in the allowlist to prevent SQL injection.
      if (!ALLOWED_SQL_OPERATORS.has(String(f.operator).toUpperCase())) return;
      const alias = result.aliasMap[f.tablePath] || 't0';
      let val;
      if (f.operator === 'IN') {
        const items = f.value.split(',').map(v => v.trim()).filter(Boolean)
          .map(v => isNaN(v) ? `'${v.replace(/'/g, "''")}'` : v);
        val = `(${items.join(', ')})`;
      } else {
        const isStringOp = f.operator === 'LIKE' || f.operator === 'ILIKE';
        val = isStringOp
          ? `'${f.value.replace(/'/g, "''")}'`
          : (isNaN(f.value) ? `'${f.value.replace(/'/g, "''")}'` : f.value);
      }
      whereClauses.push(`${alias}."${f.column}" ${f.operator} ${val}`);
    }
  });
  if (whereClauses.length > 0) sql += '\nWHERE ' + whereClauses.join('\n  AND ');

  // ORDER BY + LIMIT/OFFSET (deterministic paging)
  // Mirror backend logic: append every column of the root view as ASC
  // tie-breakers so OFFSET pages are stable even when the primary sort
  // column has duplicate values.
  const hasLimit = queryMode.type === 'first' || queryMode.type === 'last';
  const offset = Math.max(0, parseInt(queryMode.offset || 0, 10) || 0);
  const needsOrder = hasLimit || offset > 0;
  if (needsOrder) {
    const allRootCols = ((columnsCache && columnsCache[rootTable]) || [])
      .map(c => (c && (c.COLUMN_NAME || c.name)) || null)
      .filter(Boolean);
    const orderParts = [];
    const seen = new Set();
    const pushCol = (name, dir) => {
      const key = String(name).toUpperCase();
      if (seen.has(key)) return;
      seen.add(key);
      orderParts.push(`t0."${name}" ${dir}`);
    };
    if (queryMode.orderBy) {
      const dir = (queryMode.orderDir || 'ASC').toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
      pushCol(queryMode.orderBy, dir);
    } else if (queryMode.type === 'last') {
      if (allRootCols.length > 0) pushCol(allRootCols[0], 'DESC');
      else orderParts.push('1 DESC');
    }
    allRootCols.forEach(c => pushCol(c, 'ASC'));
    if (orderParts.length === 0) orderParts.push('1 ASC');
    sql += '\nORDER BY ' + orderParts.join(', ');
  }
  if (hasLimit) sql += `\nLIMIT ${queryMode.count}`;
  if (offset > 0) sql += `\nOFFSET ${offset}`;

  return { sql, columnMapping: result.columnMapping };
}

/**
 * Build a GraphQL-style query string from UI state.
 * Uses view labels for readability; includes `on:` arg for JOIN disambiguation.
 */
function buildGraphQLQuery(rootTable, selectedFields, expandedRelations, filters, queryMode, views) {
  if (!rootTable) return '';
  const rootView = views[rootTable];
  if (!rootView) return '';

  // Use label if unique across views, otherwise fall back to node ID
  const labelCounts = {};
  Object.values(views).forEach(v => { labelCounts[v.label] = (labelCounts[v.label] || 0) + 1; });
  const rootName = labelCounts[rootView.label] === 1 ? rootView.label : rootTable;

  /** Get filters applicable to a specific field under a given path */
  function getFieldFilter(path, column) {
    const pf = (filters || []).filter(f =>
      f.tablePath === path && f.column === column && f.operator && f.value !== undefined && f.value !== ''
    );
    if (pf.length === 0) return '';
    // Only one filter per field in this syntax
    const f = pf[0];
    return ` (filter: {${f.operator}: "${f.value}"})`;
  }

  function buildBlock(path, nodeId, depth) {
    const indent = '  '.repeat(depth);
    const fields = selectedFields[path] || [];
    const expanded = expandedRelations[path] || [];
    const lines = [];
    const currentView = views[nodeId];
    const currentLabel = currentView ? currentView.label : nodeId;

    for (const col of fields) {
      lines.push(`${indent}${col}${getFieldFilter(path, col)}`);
    }

    for (const rel of expanded) {
      const targetView = views[rel.target];
      if (!targetView) continue;
      const childPath = makePathKey(path, rel.target, rel.joinColumn);
      const argParts = [`join_on: ${rel.joinColumn}`];
      const childBody = buildBlock(childPath, rel.target, depth + 1);
      const segName = nestSegmentName(rel, currentLabel, targetView.label);
      lines.push(`${indent}${segName}(${argParts.join(', ')}) {`);
      if (childBody) lines.push(childBody);
      lines.push(`${indent}}`);
    }

    return lines.join('\n');
  }

  const rootArgParts = [];
  if (queryMode.type === 'first') rootArgParts.push(`first: ${queryMode.count}`);
  else if (queryMode.type === 'last') rootArgParts.push(`last: ${queryMode.count}`);
  const offset = Math.max(0, parseInt(queryMode.offset || 0, 10) || 0);
  if (offset > 0) rootArgParts.push(`offset: ${offset}`);
  if (queryMode.orderBy) {
    rootArgParts.push(`order_by: ${queryMode.orderBy}`);
    const dir = (queryMode.orderDir || 'ASC').toUpperCase();
    rootArgParts.push(`order_dir: ${dir === 'DESC' ? 'DESC' : 'ASC'}`);
  }
  const argsStr = rootArgParts.length > 0 ? `(${rootArgParts.join(', ')})` : '';

  const body = buildBlock(rootTable, rootTable, 2);
  return `{\n  ${rootName}${argsStr} {\n${body}\n  }\n}`;
}


// ─── JSON Tree Viewer (collapsible, syntax-highlighted) ───────────────

function JsonTreeViewer({ data, defaultExpanded = 2 }) {
  return <JsonNode data={data} depth={0} objKey={null} defaultExpanded={defaultExpanded} last />;
}

function JsonNode({ data, depth, objKey, defaultExpanded, last }) {
  const [expanded, setExpanded] = useState(depth < defaultExpanded);
  const indent = depth * 18;

  if (data === null) return (
    <div className="jtv-line" style={{ paddingLeft: indent }}>
      {objKey !== null && <><span className="jtv-key">"{objKey}"</span><span className="jtv-colon">: </span></>}
      <span className="jtv-null">null</span>{!last && <span className="jtv-comma">,</span>}
    </div>
  );
  if (typeof data === 'boolean') return (
    <div className="jtv-line" style={{ paddingLeft: indent }}>
      {objKey !== null && <><span className="jtv-key">"{objKey}"</span><span className="jtv-colon">: </span></>}
      <span className="jtv-bool">{String(data)}</span>{!last && <span className="jtv-comma">,</span>}
    </div>
  );
  if (typeof data === 'number') return (
    <div className="jtv-line" style={{ paddingLeft: indent }}>
      {objKey !== null && <><span className="jtv-key">"{objKey}"</span><span className="jtv-colon">: </span></>}
      <span className="jtv-num">{data}</span>{!last && <span className="jtv-comma">,</span>}
    </div>
  );
  if (typeof data === 'string') return (
    <div className="jtv-line" style={{ paddingLeft: indent }}>
      {objKey !== null && <><span className="jtv-key">"{objKey}"</span><span className="jtv-colon">: </span></>}
      <span className="jtv-str">"{data}"</span>{!last && <span className="jtv-comma">,</span>}
    </div>
  );

  const isArray = Array.isArray(data);
  const entries = isArray ? data.map((v, i) => [i, v]) : Object.entries(data);
  const openBrace = isArray ? '[' : '{';
  const closeBrace = isArray ? ']' : '}';
  const count = entries.length;

  if (count === 0) return (
    <div className="jtv-line" style={{ paddingLeft: indent }}>
      {objKey !== null && <><span className="jtv-key">"{objKey}"</span><span className="jtv-colon">: </span></>}
      <span className="jtv-bracket">{openBrace}{closeBrace}</span>{!last && <span className="jtv-comma">,</span>}
    </div>
  );

  return (
    <div>
      <div className="jtv-line jtv-toggle" style={{ paddingLeft: indent }} onClick={() => setExpanded(e => !e)}>
        {objKey !== null && <><span className="jtv-key">"{objKey}"</span><span className="jtv-colon">: </span></>}
        <span className="jtv-bracket">{openBrace}</span>
        {!expanded && <span className="jtv-collapsed"> {count} {isArray ? 'items' : 'keys'} {closeBrace}</span>}
        {!expanded && !last && <span className="jtv-comma">,</span>}
        <span className="jtv-arrow">{expanded ? <DownOutlined /> : <RightOutlined />}</span>
      </div>
      {expanded && (
        <>
          {entries.map(([key, val], i) => (
            <JsonNode
              key={key}
              data={val}
              depth={depth + 1}
              objKey={isArray ? null : key}
              defaultExpanded={defaultExpanded}
              last={i === count - 1}
            />
          ))}
          <div className="jtv-line" style={{ paddingLeft: indent }}>
            <span className="jtv-bracket">{closeBrace}</span>{!last && <span className="jtv-comma">,</span>}
          </div>
        </>
      )}
    </div>
  );
}


// ─── Inline Filter Popover ────────────────────────────────────────────

/** Extract VALUES:[...] from a column description string. */
function parseValuesFromDescription(desc) {
  if (!desc) return [];
  const match = desc.match(/VALUES:\s*\[([^\]]+)\]/i);
  if (!match) return [];
  return match[1].split(',').map(v => v.trim().replace(/^['"]|['"]$/g, '')).filter(Boolean);
}

/** Build autocomplete suggestions based on column type and description. */
function getFilterSuggestions(columnType, columnDescription) {
  const type = (columnType || '').toUpperCase();
  // BOOLEAN → TRUE / FALSE
  if (type === 'BOOLEAN') {
    return [{ value: 'TRUE' }, { value: 'FALSE' }];
  }
  // TIMESTAMP variants → current timestamp suggestions
  if (type.startsWith('TIMESTAMP') || type === 'DATE') {
    const now = new Date();
    const isoFull = now.toISOString().replace('T', ' ').substring(0, 19);
    const isoDate = now.toISOString().substring(0, 10);
    return [
      { value: isoFull, label: `Now: ${isoFull}` },
      { value: isoDate, label: `Today: ${isoDate}` },
    ];
  }
  // VALUES:[...] from description
  const vals = parseValuesFromDescription(columnDescription);
  if (vals.length > 0) {
    return vals.map(v => ({ value: v }));
  }
  return [];
}

function InlineFilterPopover({ tablePath, columnName, columnType, columnDescription, filters, onAddFilter, onRemoveFilter, onUpdateFilter, children }) {
  const [open, setOpen] = useState(false);
  const existing = filters.filter(f => f.tablePath === tablePath && f.column === columnName);

  const existing0 = existing[0];
  const globalIdx = existing0 ? filters.indexOf(existing0) : -1;

  // Auto-create filter when popover opens and no filter exists yet
  const handleOpenChange = (visible) => {
    if (visible && !existing0) {
      onAddFilter(tablePath, columnName);
    }
    setOpen(visible);
  };

  // Resolve the correct index — after auto-create the new filter is at the end
  const editIdx = globalIdx >= 0 ? globalIdx : filters.length;

  const suggestions = useMemo(() => getFilterSuggestions(columnType, columnDescription), [columnType, columnDescription]);

  const valueInput = suggestions.length > 0 ? (
    <AutoComplete
      size="small"
      value={existing0?.value || ''}
      onChange={val => onUpdateFilter(editIdx, 'value', val)}
      placeholder={(existing0?.operator || '=') === 'IN' ? 'val1, val2, ...' : 'value'}
      style={{ width: 130 }}
      options={suggestions}
      filterOption={(input, option) =>
        (option?.value ?? '').toLowerCase().includes((input || '').toLowerCase())
      }
    />
  ) : (
    <Input
      size="small"
      value={existing0?.value || ''}
      onChange={e => onUpdateFilter(editIdx, 'value', e.target.value)}
      placeholder={(existing0?.operator || '=') === 'IN' ? 'val1, val2, ...' : 'value'}
      style={{ width: 130 }}
    />
  );

  const content = (
    <div style={{ minWidth: 240 }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <Select
          size="small"
          value={existing0?.operator || '='}
          onChange={val => onUpdateFilter(editIdx, 'operator', val)}
          style={{ width: 80 }}
          options={[
            { value: '=', label: '=' },
            { value: '!=', label: '!=' },
            { value: '>', label: '>' },
            { value: '<', label: '<' },
            { value: '>=', label: '>=' },
            { value: '<=', label: '<=' },
            { value: 'LIKE', label: 'LIKE' },
            { value: 'ILIKE', label: 'ILIKE' },
            { value: 'IN', label: 'IN' },
          ]}
        />
        {valueInput}
        <Tooltip title="Remove filter">
          <Button size="small" danger type="text" onClick={() => { onRemoveFilter(editIdx); setOpen(false); }}>✕</Button>
        </Tooltip>
        <Tooltip title="Done">
          <Button size="small" type="primary" style={{ minWidth: 28, padding: '0 4px' }} onClick={() => setOpen(false)}>✓</Button>
        </Tooltip>
      </div>
    </div>
  );

  return (
    <Popover content={content} title={`Filter: ${columnName}`} trigger="click" placement="rightTop" open={open} onOpenChange={handleOpenChange}>
      {children}
    </Popover>
  );
}


// ─── Collapsible Reverse Relations Section ────────────────────────────

function ReverseRelationsSection({ orphanReverseRels, renderRelation, indents, viewLabel }) {
  const [collapsed, setCollapsed] = useState(true);

  return (
    <>
      <div className="tree-line" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setCollapsed(c => !c)}>
        {indents}
        <Text type="secondary" style={{ fontSize: 11, color: '#389e0d' }}>
          {collapsed
            ? `Show entities refer to ${viewLabel}`
            : `${orphanReverseRels.length} reverse relationship${orphanReverseRels.length > 1 ? 's' : ''}`}
        </Text>
        <span style={{ fontSize: 10, marginLeft: 4, color: '#389e0d' }}>
          {collapsed ? <RightOutlined /> : <DownOutlined />}
        </span>
      </div>
      {!collapsed && orphanReverseRels.map((rel, relIdx) => renderRelation(rel, relIdx, 'rev', indents))}
    </>
  );
}

// ─── Collapsible per-column Reverse Relations ─────────────────────────

function ColumnReverseSection({ reverseRels, renderRelation, indents, viewLabel, columnName }) {
  const [collapsed, setCollapsed] = useState(true);

  return (
    <>
      <div className="tree-line" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setCollapsed(c => !c)}>
        {indents}
        <Text type="secondary" style={{ fontSize: 11, color: '#16a34a' }}>
          {collapsed
            ? `Show entities refer to ${viewLabel}.${columnName}`
            : `${reverseRels.length} referring entit${reverseRels.length > 1 ? 'ies' : 'y'}`}
        </Text>
        <span style={{ fontSize: 10, marginLeft: 4, color: '#16a34a' }}>
          {collapsed ? <RightOutlined /> : <DownOutlined />}
        </span>
      </div>
      {!collapsed && reverseRels.map((rel, relIdx) => renderRelation(rel, relIdx, 'colrev', indents))}
    </>
  );
}


// ─── Recursive Table Tree Renderer ────────────────────────────────────

function TableTreeNode({
  schema, nodeId, path, depth,
  columnsCache, loadingColumns, loadColumns,
  selectedFields, toggleField, toggleAllFields,
  expandedRelations, toggleRelation,
  filters, addFilter, removeFilter, updateFilter,
  collapsedPaths, setCollapsedPaths,
}) {
  const collapsed = !!collapsedPaths[path];
  const columns = columnsCache[nodeId] || [];
  const view = schema.views[nodeId];
  if (!view) return null;

  // Find relations FROM this node (both forward FK and reverse/back-references)
  const relations = schema.relationships.filter(r => r.source === nodeId);

  // Build a map: column_name → [relations that use this column as joinColumn]
  const colRelMap = {};
  // Reverse relations whose joinColumn doesn't exist as a column on this table
  // are shown in a dedicated collapsible section below.
  const orphanReverseRels = [];
  const columnNames = new Set(columns.map(c => c.COLUMN_NAME));
  relations.forEach(rel => {
    if (rel.reverse && !columnNames.has(rel.joinColumn)) {
      orphanReverseRels.push(rel);
    } else {
      if (!colRelMap[rel.joinColumn]) colRelMap[rel.joinColumn] = [];
      colRelMap[rel.joinColumn].push(rel);
    }
  });

  const isExpanded = (rel) => {
    return (expandedRelations[path] || []).find(
      r => r.target === rel.target && r.joinColumn === rel.joinColumn
    );
  };

  /** Build display label for a relationship */
  const relLabel = (rel) => {
    const targetView = schema.views[rel.target];
    if (!targetView) return '';
    const relType = rel.relationship || 'has many';
    if (rel.reverse) {
      // <DESTINATION>_relationship_<MAIN>
      return `${targetView.label}_${relType}_${view.label}`;
    }
    // relationship_<OBJECT>
    return `${relType}_${targetView.label}`;
  };

  const indents = Array.from({ length: depth + 2 }, (_, i) => <span key={i} className="tree-indent" />);
  const childIndents = Array.from({ length: depth + 3 }, (_, i) => <span key={i} className="tree-indent" />);

  const selectedCount = (selectedFields[path] || []).length;

  /** Render an expandable relation block (shared between column-attached and orphan relations) */
  const renderRelation = (rel, relIdx, keyPrefix, extraIndents) => {
    const targetView = schema.views[rel.target];
    if (!targetView) return null;
    const expanded = isExpanded(rel);
    const childPath = makePathKey(path, rel.target, rel.joinColumn);
    const isReverse = !!rel.reverse;
    const targetDenied = !!targetView.access_denied;
    const denyMsg = targetDenied
      ? `You don't have permission to access ${targetView.label}. Contact an administrator to request access.`
      : null;

    return (
      <React.Fragment key={`${keyPrefix}-${rel.target}-${rel.joinColumn}-${relIdx}`}>
        <div className="tree-line relation-line" style={targetDenied ? { opacity: 0.5 } : undefined}>
          {extraIndents}
          <Checkbox
            className="field-check"
            checked={!!expanded}
            disabled={targetDenied}
            onChange={() => {
              if (targetDenied) return;
              toggleRelation(path, rel);
              loadColumns(rel.target);
            }}
          />
          <Tag color={isReverse ? 'green' : 'blue'} style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', marginRight: 4 }}>
            {isReverse ? '1:N' : '1:1'}
          </Tag>
          <Tooltip title={denyMsg || ''} placement="right">
          <span
            className="tree-relation"
            style={targetDenied ? { cursor: 'not-allowed' } : undefined}
            onClick={() => {
              if (targetDenied) return;
              toggleRelation(path, rel);
              loadColumns(rel.target);
            }}
          >
            <span className="relation-badge" style={{ color: isReverse ? '#16a34a' : '#3b82f6' }}>
              {targetDenied && <LockOutlined style={{ marginRight: 4 }} />}
              {relLabel(rel)}
            </span>
          </span>
          </Tooltip>
          {expanded && (
            <span className="tree-bracket" style={{ marginLeft: 4 }}>{'{'}</span>
          )}
          <span
            className="tree-collapse-toggle"
            style={{ fontSize: 10, marginLeft: 'auto', color: '#8250df', cursor: 'pointer', paddingRight: 8 }}
            onClick={(e) => { e.stopPropagation(); setCollapsedPaths(prev => ({ ...prev, [childPath]: !prev[childPath] })); }}
          >
            {expanded ? (collapsedPaths[childPath] ? <RightOutlined /> : <DownOutlined />) : null}
          </span>
        </div>

        {expanded && (
          <>
            <div className="tree-line">
              {childIndents}
              <Button
                type="link"
                size="small"
                style={{ fontSize: 11, padding: '0 4px' }}
                onClick={(e) => { e.stopPropagation(); toggleAllFields(childPath, rel.target); }}
              >
                {(selectedFields[childPath] || []).length === (columnsCache[rel.target] || []).length && (columnsCache[rel.target] || []).length > 0
                  ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            <div className="query-tree-group" style={{ '--tree-line-left': `${(depth + 2 + 2) * 20 + 8}px` }}>
            <TableTreeNode
              schema={schema}
              nodeId={rel.target}
              path={childPath}
              depth={depth + 2}
              columnsCache={columnsCache}
              loadingColumns={loadingColumns}
              loadColumns={loadColumns}
              selectedFields={selectedFields}
              toggleField={toggleField}
              toggleAllFields={toggleAllFields}
              expandedRelations={expandedRelations}
              toggleRelation={toggleRelation}
              filters={filters}
              addFilter={addFilter}
              removeFilter={removeFilter}
              updateFilter={updateFilter}
              collapsedPaths={collapsedPaths}
              setCollapsedPaths={setCollapsedPaths}
            />
            </div>

            <div className="tree-line">
              {childIndents}
              <span className="tree-bracket">{'}'}</span>
            </div>
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      {loadingColumns[nodeId] ? (
        <div className="tree-line">
          {indents}
          <Spin size="small" /> <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>Loading columns...</Text>
        </div>
      ) : (
        <>
          {/* Collapse / Expand toggle for columns — shown only when collapsed */}
          {columns.length > 0 && collapsed && (
            <div className="tree-line" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setCollapsedPaths(prev => ({ ...prev, [path]: false }))}>
              {indents}
              <Text type="secondary" style={{ fontSize: 11 }}>
                {`${columns.length} columns${selectedCount > 0 ? ` (${selectedCount} selected)` : ''} ...`}
              </Text>
              <span style={{ fontSize: 10, marginLeft: 4, color: '#8250df' }}>
                <RightOutlined />
              </span>
            </div>
          )}

          {!collapsed && columns.map(col => {
            const colRels = colRelMap[col.COLUMN_NAME] || [];
            const hasFilter = filters.some(f => f.tablePath === path && f.column === col.COLUMN_NAME);
            const activeFilters = filters.filter(f => f.tablePath === path && f.column === col.COLUMN_NAME);
            return (
              <React.Fragment key={col.COLUMN_NAME}>
                <div className={`tree-line field-line ${hasFilter ? 'has-filter' : ''}`}>
                  {indents}
                  <Checkbox
                    className="field-check"
                    checked={(selectedFields[path] || []).includes(col.COLUMN_NAME)}
                    onChange={() => toggleField(path, col.COLUMN_NAME)}
                  />
                  <InlineFilterPopover
                    tablePath={path}
                    columnName={col.COLUMN_NAME}
                    columnType={col.TYPE}
                    columnDescription={col.COLUMN_DESCRIPTION}
                    filters={filters}
                    onAddFilter={addFilter}
                    onRemoveFilter={removeFilter}
                    onUpdateFilter={updateFilter}
                  >
                    <span className="field-name-filter" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Tooltip title={col.COLUMN_DESCRIPTION || undefined} placement="top" mouseEnterDelay={0.4}>
                        <span className="tree-key">{col.COLUMN_NAME}</span>
                      </Tooltip>
                      <FilterOutlined style={{ fontSize: 10, opacity: hasFilter ? 1 : 0.2, color: hasFilter ? '#fa8c16' : undefined }} />
                    </span>
                  </InlineFilterPopover>
                  <span className="tree-type">{col.TYPE}</span>
                </div>
                {/* Show active filter summary inline */}
                {activeFilters.map((f, fi) => (
                  <div key={fi} className="tree-line filter-summary-line">
                    {indents}
                    <span className="tree-indent" />
                    <Tag color="orange" style={{ fontSize: 10, lineHeight: '16px', padding: '0 6px' }}>
                      {f.operator} {f.value}
                    </Tag>
                  </div>
                ))}

                {/* Show expandable 1:1 relations right below the FK column */}
                {colRels.filter(r => !r.reverse).map((rel, relIdx) => renderRelation(rel, relIdx, 'col', <>{indents}<span className="tree-indent" /></>))}

                {/* Collapsible 1:N reverse relations for this column */}
                {colRels.filter(r => r.reverse).length > 0 && (
                  <ColumnReverseSection
                    reverseRels={colRels.filter(r => r.reverse)}
                    renderRelation={renderRelation}
                    indents={<>{indents}<span className="tree-indent" /></>}
                    viewLabel={view.label}
                    columnName={col.COLUMN_NAME}
                  />
                )}
              </React.Fragment>
            );
          })}

          {/* ── Orphan reverse (1:N) relations without a matching column ── */}
          {orphanReverseRels.length > 0 && (
            <ReverseRelationsSection
              orphanReverseRels={orphanReverseRels}
              renderRelation={renderRelation}
              indents={indents}
              viewLabel={view.label}
            />
          )}
        </>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TABLE TREE ITEM (recursive for SAT/LINK nesting under HUB tables)
// ═══════════════════════════════════════════════════════════════════════

function TableTreeItem({ table, selectedTable, onSelectTable }) {
  const denied = !!table.access_denied;
  const tooltipText = denied
    ? `You don't have permission to access ${table.id}. Contact an administrator to request access.`
    : table.id;
  return (
    <div className="table-tree-item">
      <div
        className={`table-item ${selectedTable === table.id ? 'selected' : ''} ${denied ? 'access-denied' : ''}`}
        onClick={() => { if (!denied) onSelectTable(table.id); }}
        style={denied ? { cursor: 'not-allowed', color: '#c8c8c8', background: 'transparent' } : undefined}
        aria-disabled={denied || undefined}
      >
        <span className="table-radio" style={denied ? { borderColor: '#dcdcdc', background: '#f5f5f5' } : undefined} />
        <span className="table-label" style={denied ? { color: '#c8c8c8', fontStyle: 'italic' } : undefined}>
          <Tooltip title={tooltipText} placement="right">
            <span>
              {denied && <LockOutlined style={{ marginRight: 4, color: '#c8c8c8' }} />}
              {table.label}
            </span>
          </Tooltip>
        </span>
      </div>
      {table.childTables && table.childTables.length > 0 && (
        <div className="schema-tree-children">
          {table.childTables.map(child => (
            <TableTreeItem key={child.id} table={child} selectedTable={selectedTable} onSelectTable={onSelectTable} />
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SCHEMA TREE NODE (recursive component for left panel)
// ═══════════════════════════════════════════════════════════════════════

function SchemaTreeNode({ node, depth, selectedTable, onSelectTable, expandedSchemas, toggleSchemaNode, searchText }) {
  const isExpanded = expandedSchemas[node.fullPath] ?? true;
  const hasContent = (node.childrenArr && node.childrenArr.length > 0) || node.tables.length > 0;
  const nestedTables = useMemo(() => nestTables(node.tables), [node.tables]);

  // A schema is "all denied" when every table in it (and every descendant
  // schema) is access_denied. Render those headers in a lighter grey so
  // users can scan quickly for schemas where nothing is selectable.
  const allDenied = useMemo(() => {
    if (!hasContent) return false;
    const tablesAllDenied = node.tables.length === 0 || node.tables.every(t => t.access_denied);
    const childrenAllDenied = (node.childrenArr || []).every(c => {
      const childTablesDenied = c.tables.length === 0 || c.tables.every(t => t.access_denied);
      return childTablesDenied && (c.childrenArr || []).length === 0;
    });
    return tablesAllDenied && childrenAllDenied;
  }, [node, hasContent]);

  return (
    <div className="schema-tree-node">
      <div
        className="schema-tree-header"
        onClick={() => toggleSchemaNode(node.fullPath)}
        style={allDenied ? { color: '#c8c8c8' } : undefined}
      >
        <span className="schema-tree-toggle" style={allDenied ? { color: '#d9d9d9' } : undefined}>
          {hasContent ? (isExpanded ? <DownOutlined /> : <RightOutlined />) : null}
        </span>
        <span className="schema-tree-name" style={allDenied ? { color: '#c8c8c8', fontStyle: 'italic' } : undefined}>
          {allDenied && <LockOutlined style={{ marginRight: 4, color: '#c8c8c8' }} />}
          {node.name}
        </span>
      </div>
      {isExpanded && (
        <div className="schema-tree-children">
          {/* Tables above child schemas */}
          {nestedTables.map(t => (
            <TableTreeItem key={t.id} table={t} selectedTable={selectedTable} onSelectTable={onSelectTable} />
          ))}
          {node.childrenArr.map(child => (
            <SchemaTreeNode
              key={child.fullPath}
              node={child}
              depth={depth + 1}
              selectedTable={selectedTable}
              onSelectTable={onSelectTable}
              expandedSchemas={expandedSchemas}
              toggleSchemaNode={toggleSchemaNode}
              searchText={searchText}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export default function GraphQLBuilder() {
  const { userEmail } = useAuth();

  // ─── Schema state ──────────────────────────────────────────────────
  const [schema, setSchema] = useState(null);
  const [columnsCache, setColumnsCache] = useState({});
  const [loadingSchema, setLoadingSchema] = useState(true);
  const [loadingColumns, setLoadingColumns] = useState({});

  // ─── API Key state ─────────────────────────────────────────────────
  const [apiKeyInfo, setApiKeyInfo] = useState(null);   // { exists, key_prefix, created_at, last_used_at }
  const [apiKeyFull, setApiKeyFull] = useState(() => {
    try { return localStorage.getItem('graphql_api_key') || null; } catch { return null; }
  });
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);

  // ─── Query builder state ───────────────────────────────────────────
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedFields, setSelectedFields] = useState({});       // pathKey → [col1,…]
  const [expandedRelations, setExpandedRelations] = useState({}); // pathKey → [rel,…]
  const [filters, setFilters] = useState([]);                      // [{ tablePath, column, operator, value }]
  const [collapsedPaths, setCollapsedPaths] = useState({});         // pathKey → boolean
  const [queryMode, setQueryMode] = useState({ type: 'first', count: 50, offset: 0, orderBy: null, orderDir: 'ASC' });
  const [searchText, setSearchText] = useState('');
  const [onlyAccessible, setOnlyAccessible] = useState(false);

  // ─── Response state ────────────────────────────────────────────────
  const [response, setResponse] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [previewMode, setPreviewMode] = useState('json'); // 'json' or 'sql'
  const [previewExpanded, setPreviewExpanded] = useState(false);

  // ─── Load schema on mount ──────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const data = await getGraphQLSchema();
        setSchema(data);
      } catch (err) {
        message.error('Failed to load schema: ' + (err.message || err));
      } finally {
        setLoadingSchema(false);
      }
    })();
  }, []);

  // ─── Load API key info on mount ────────────────────────────────────
  const loadApiKeyInfo = useCallback(async () => {
    if (!userEmail) return;
    try {
      const data = await getGraphQLApiKey(userEmail);
      setApiKeyInfo(data);
    } catch { /* ignore */ }
  }, [userEmail]);

  useEffect(() => { loadApiKeyInfo(); }, [loadApiKeyInfo]);

  const handleCreateApiKey = useCallback(async () => {
    if (!userEmail) { message.warning('You must be logged in'); return; }
    setApiKeyLoading(true);
    try {
      const data = await createGraphQLApiKey(userEmail);
      setApiKeyFull(data.full_key);
      try { localStorage.setItem('graphql_api_key', data.full_key); } catch {}
      setApiKeyInfo({ exists: true, key_prefix: data.key_prefix, created_at: data.created_at });
      setApiKeyVisible(true);
      message.success('API key created — stored locally for convenience.');
    } catch (err) {
      message.error('Failed to create API key: ' + (err.message || err));
    } finally {
      setApiKeyLoading(false);
    }
  }, [userEmail]);

  const handleDeleteApiKey = useCallback(async () => {
    if (!userEmail) return;
    Modal.confirm({
      title: 'Delete API Key',
      content: 'This will permanently revoke the key. Any external integrations using it will stop working.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        setApiKeyLoading(true);
        try {
          await deleteGraphQLApiKey(userEmail);
          setApiKeyInfo({ exists: false });
          setApiKeyFull(null);
          try { localStorage.removeItem('graphql_api_key'); } catch {}
          message.success('API key deleted');
        } catch (err) {
          message.error('Failed to delete key: ' + (err.message || err));
        } finally {
          setApiKeyLoading(false);
        }
      },
    });
  }, [userEmail]);

  const handleResetApiKey = useCallback(async () => {
    if (!userEmail) return;
    Modal.confirm({
      title: 'Reset API Key',
      content: 'This will revoke the current key and generate a new one. Any existing integrations will need to be updated.',
      okText: 'Reset',
      onOk: () => handleCreateApiKey(),
    });
  }, [userEmail, handleCreateApiKey]);

  // ─── Load columns for a view ───────────────────────────────────────
  const loadColumns = useCallback(async (nodeId) => {
    if (columnsCache[nodeId] || loadingColumns[nodeId]) return;
    if (!schema) return;
    const view = schema.views[nodeId];
    if (!view) return;

    const qualified = getQualifiedName(schema.views, nodeId);
    setLoadingColumns(prev => ({ ...prev, [nodeId]: true }));
    try {
      const data = await getCatalogViews([qualified]);
      const viewData = (data.views || [])[0];
      if (viewData && viewData.COLUMNS) {
        setColumnsCache(prev => ({ ...prev, [nodeId]: viewData.COLUMNS }));
      }
    } catch {
      // silently fail
    } finally {
      setLoadingColumns(prev => ({ ...prev, [nodeId]: false }));
    }
  }, [schema, columnsCache, loadingColumns]);

  // ─── When user selects a root table ────────────────────────────────
  const handleSelectTable = useCallback((nodeId) => {
    const v = schema && schema.views && schema.views[nodeId];
    if (v && v.access_denied) {
      message.warning(`You don't have permission to access ${v.label}. Contact an administrator to request access.`);
      return;
    }
    setSelectedTable(nodeId);
    setSelectedFields({});
    setExpandedRelations({});
    setFilters([]);
    setResponse(null);
    loadColumns(nodeId);
  }, [loadColumns, schema]);

  // ─── Toggle field (path-aware) ─────────────────────────────────────
  const toggleField = useCallback((path, columnName) => {
    setSelectedFields(prev => {
      const current = prev[path] || [];
      const next = current.includes(columnName)
        ? current.filter(c => c !== columnName)
        : [...current, columnName];
      return { ...prev, [path]: next };
    });
  }, []);

  const toggleAllFields = useCallback((path, nodeId) => {
    const cols = columnsCache[nodeId] || [];
    setSelectedFields(prev => {
      const current = prev[path] || [];
      if (current.length === cols.length) {
        return { ...prev, [path]: [] };
      }
      return { ...prev, [path]: cols.map(c => c.COLUMN_NAME) };
    });
  }, [columnsCache]);

  // ─── Toggle relation expansion (path-aware) ────────────────────────
  const toggleRelation = useCallback((parentPath, rel) => {
    setExpandedRelations(prev => {
      const current = prev[parentPath] || [];
      const exists = current.find(r => r.target === rel.target && r.joinColumn === rel.joinColumn);
      if (exists) {
        return { ...prev, [parentPath]: current.filter(r => r !== exists) };
      }
      return { ...prev, [parentPath]: [...current, rel] };
    });
  }, []);

  // ─── Filter management (tablePath-aware) ───────────────────────────
  const addFilter = useCallback((tablePath, column) => {
    setFilters(prev => [...prev, { tablePath, column, operator: '=', value: '' }]);
  }, []);

  const updateFilter = useCallback((idx, field, value) => {
    setFilters(prev => prev.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  }, []);

  const removeFilter = useCallback((idx) => {
    setFilters(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // ─── Get relationships for a node ──────────────────────────────────
  const getRelationships = useCallback((nodeId) => {
    if (!schema) return [];
    return schema.relationships.filter(r => r.source === nodeId);
  }, [schema]);

  // ─── Compute SQL (for preview) + query payload (for execution) ─────
  const generatedSQL = useMemo(() => {
    if (!schema || !selectedTable) return '';
    return buildSQL(selectedTable, selectedFields, expandedRelations, filters, queryMode, schema.views, schema.relationships, columnsCache).sql;
  }, [schema, selectedTable, selectedFields, expandedRelations, filters, queryMode, columnsCache]);

  const graphqlQuery = useMemo(() => {
    if (!schema || !selectedTable) return '';
    return buildGraphQLQuery(selectedTable, selectedFields, expandedRelations, filters, queryMode, schema.views);
  }, [schema, selectedTable, selectedFields, expandedRelations, filters, queryMode]);

  // ─── Copy helpers ──────────────────────────────────────────────────
  const copyToClipboard = useCallback((text) => {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(text).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  }, []);

  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try { document.execCommand('copy'); } catch { /* ignore */ }
    document.body.removeChild(textarea);
  };

  const handleCopyCurl = useCallback(() => {
    const key = apiKeyFull || '<YOUR_API_KEY>';
    const baseUrl = window.location.origin;
    const compactQuery = graphqlQuery.replace(/\n\s*/g, ' ').replace(/\s+/g, ' ').trim();
    const encoded = encodeURIComponent(compactQuery);
    const curl = `curl -H "API-Key: ${key}" "${baseUrl}/api/data/graphql/execute?query=${encoded}"`;
    copyToClipboard(curl);
    message.success('cURL command copied');
  }, [apiKeyFull, graphqlQuery, copyToClipboard]);

  const handleCopyJSON = useCallback(() => {
    const compactQuery = graphqlQuery.replace(/\n\s*/g, ' ').replace(/\s+/g, ' ').trim();
    const url = `${window.location.origin}/api/data/graphql/execute?query=${encodeURIComponent(compactQuery)}`;
    copyToClipboard(url);
    message.success('Request URL copied');
  }, [graphqlQuery, copyToClipboard]);

  const handleCopySQL = useCallback(() => {
    if (generatedSQL) {
      copyToClipboard(generatedSQL);
      message.success('SQL copied');
    }
  }, [generatedSQL, copyToClipboard]);

  // ─── Execute query ─────────────────────────────────────────────────
  const handleExecute = useCallback(async () => {
    if (!graphqlQuery) { message.warning('Build a query first'); return; }
    setExecuting(true);
    setResponse(null);
    try {
      const data = await executeGraphQLQuery({ query: graphqlQuery, variables: '' });
      setResponse(data);
    } catch (err) {
      message.error('Query failed: ' + (err.message || err));
      setResponse({ error: err.message || String(err) });
    } finally {
      setExecuting(false);
    }
  }, [graphqlQuery]);

  // ─── Schema tree for table list ────────────────────────────────────
  const visibleViewCount = useMemo(() => {
    if (!schema) return 0;
    if (!onlyAccessible) return Object.keys(schema.views).length;
    return Object.values(schema.views).filter(v => !v.access_denied).length;
  }, [schema, onlyAccessible]);

  const schemaTree = useMemo(() => {
    if (!schema) return null;
    const sourceViews = onlyAccessible
      ? Object.fromEntries(Object.entries(schema.views).filter(([, v]) => !v.access_denied))
      : schema.views;
    const tree = buildSchemaTree(sourceViews);
    if (!searchText) return tree;
    return filterTree(tree, searchText.toLowerCase());
  }, [schema, searchText, onlyAccessible]);

  // Track which schema tree nodes are expanded
  const [expandedSchemas, setExpandedSchemas] = useState({});
  const toggleSchemaNode = useCallback((path) => {
    setExpandedSchemas(prev => ({ ...prev, [path]: !prev[path] }));
  }, []);

  // ─── Render ────────────────────────────────────────────────────────
  if (loadingSchema) {
    return (
      <div className="graphql-builder">
        <div className="loading-overlay"><Spin size="large" tip="Loading schema..." /></div>
      </div>
    );
  }

  if (!schema || schema.empty) {
    return (
      <div className="graphql-builder">
        <Empty description={schema?.message || "No ontology schema available. Generate ontology from catalog in the Data Ontology page first."} />
      </div>
    );
  }

  const rootColumns = selectedTable ? (columnsCache[selectedTable] || []) : [];
  const rootView = selectedTable ? schema.views[selectedTable] : null;
  const rootPath = selectedTable;

  return (
    <div className="graphql-builder">
      {/* Header */}
      <div className="builder-header">
        <h2><GlobalOutlined /> DATA PORTAL</h2>
        <p>Build and execute queries against your data catalog</p>
      </div>

      {/* ════════ API Endpoint Section (single line) ════════ */}
      <div className="endpoint-section">
        <div className="endpoint-inline-row">
          <span className="endpoint-section-title">API Endpoint</span>
          <code className="endpoint-url-text">{window.location.origin}/api/data/graphql/execute</code>
          <Tooltip title="Copy URL">
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                copyToClipboard(`${window.location.origin}/api/data/graphql/execute`);
                message.success('URL copied');
              }}
            />
          </Tooltip>
          <span className="endpoint-divider" />
          <KeyOutlined style={{ fontSize: 14, color: '#8250df' }} />
          <span className="api-key-label">API Key</span>
          {apiKeyInfo?.exists ? (
            <Tooltip
              title={
                apiKeyInfo.created_at
                  ? `Created ${new Date(apiKeyInfo.created_at).toLocaleDateString()}${apiKeyInfo.last_used_at ? ` · Last used ${new Date(apiKeyInfo.last_used_at).toLocaleDateString()}` : ''}`
                  : ''
              }
            >
              <Input.Password
                size="small"
                readOnly
                value={apiKeyFull || `${apiKeyInfo.key_prefix}${'•'.repeat(30)}`}
                visibilityToggle={{
                  visible: apiKeyVisible,
                  onVisibleChange: setApiKeyVisible,
                }}
                iconRender={v => v ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                style={{ width: 240, fontFamily: 'monospace', fontSize: 12 }}
              />
            </Tooltip>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>No API key — generate one to use this API externally</Text>
          )}
          <div className="api-key-actions">
            {apiKeyInfo?.exists ? (
              <>
                <Tooltip title="Reset key (generate new)">
                  <Button size="small" icon={<ReloadOutlined />} onClick={handleResetApiKey} loading={apiKeyLoading}>
                    Reset Key
                  </Button>
                </Tooltip>
                <Tooltip title="Delete key">
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={handleDeleteApiKey} loading={apiKeyLoading}>
                    Delete Key
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Button
                type="primary"
                size="small"
                icon={<KeyOutlined />}
                onClick={handleCreateApiKey}
                loading={apiKeyLoading}
              >
                Generate Key
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ════════ GraphQL Query Builder ════════ */}
      <div className="builder-section-header">GraphQL Query Builder</div>

      <div className="builder-body">
        {/* ════════ LEFT: Table List ════════ */}
        <div className="table-list-panel">
          <div className="panel-title">
            <DatabaseOutlined /> Tables
            <Tag color="blue" style={{ marginLeft: 'auto', fontSize: 11 }}>
              {visibleViewCount}
            </Tag>
          </div>
          <div className="table-search" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Input
              size="small"
              placeholder="Search tables..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              allowClear
            />
            <Checkbox
              checked={onlyAccessible}
              onChange={e => setOnlyAccessible(e.target.checked)}
              style={{ fontSize: 12 }}
            >
              Only display accessible tables
            </Checkbox>
          </div>
          <div className="table-list">
            {schemaTree && schemaTree.childrenArr.map(node => (
              <SchemaTreeNode
                key={node.fullPath}
                node={node}
                depth={0}
                selectedTable={selectedTable}
                onSelectTable={handleSelectTable}
                expandedSchemas={expandedSchemas}
                toggleSchemaNode={toggleSchemaNode}
                searchText={searchText}
              />
            ))}
            {/* Tables at root level (OTHER) */}
            {schemaTree && nestTables(schemaTree.tables).map(t => (
              <TableTreeItem key={t.id} table={t} selectedTable={selectedTable} onSelectTable={handleSelectTable} />
            ))}
          </div>
        </div>

        {/* ════════ CENTER: Query Builder ════════ */}
        <div className="query-builder-panel">
          <div className="panel-toolbar">
            <span className="toolbar-title"><CodeOutlined /> Query</span>
            {filters.length > 0 && (
              <Tag color="orange" style={{ fontSize: 11 }}>
                <FilterOutlined /> {filters.length} filter{filters.length > 1 ? 's' : ''}
              </Tag>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              {apiKeyInfo?.exists && (
                <Tooltip title="Copy as cURL">
                  <Button size="small" icon={<CopyOutlined />} onClick={handleCopyCurl} disabled={!graphqlQuery}>
                    Copy as cURL
                  </Button>
                </Tooltip>
              )}
              <Button
                type="primary"
                size="small"
                icon={<PlayCircleOutlined />}
                loading={executing}
                onClick={handleExecute}
                disabled={!graphqlQuery}
              >
                Run
              </Button>
            </div>
          </div>
          <div className="query-variables-bar">
            <span className="variables-label">Variables</span>
            <div className="variable-field">
              <span className="variable-field-label">Order</span>
              <Select
                size="small"
                value={queryMode.type}
                onChange={val => setQueryMode(prev => ({ ...prev, type: val }))}
                style={{ width: 100 }}
                options={[
                  { value: 'first', label: 'First N' },
                  { value: 'last', label: 'Last N' },
                  { value: 'all', label: 'All rows' },
                ]}
              />
            </div>
            {queryMode.type !== 'all' && (
              <>
                <div className="variable-field">
                  <span className="variable-field-label">Page size (rows)</span>
                  <InputNumber
                    size="small"
                    min={1}
                    max={5000}
                    value={queryMode.count}
                    onChange={val => setQueryMode(prev => ({ ...prev, count: val || 50, offset: 0 }))}
                    style={{ width: 90 }}
                  />
                </div>
                <div className="variable-field">
                  <Tooltip title="Skip this many rows before returning. Backend caps a single page at 5000 rows.">
                    <span className="variable-field-label">Offset</span>
                  </Tooltip>
                  <InputNumber
                    size="small"
                    min={0}
                    step={queryMode.count || 50}
                    value={queryMode.offset || 0}
                    onChange={val => setQueryMode(prev => ({ ...prev, offset: Math.max(0, val || 0) }))}
                    style={{ width: 90 }}
                  />
                </div>
                <div className="variable-field">
                  <Tooltip title="Prev / Next page (steps by Page size)">
                    <span className="variable-field-label">Page</span>
                  </Tooltip>
                  <Button.Group size="small">
                    <Button
                      icon={<LeftOutlined />}
                      disabled={(queryMode.offset || 0) <= 0}
                      onClick={() => setQueryMode(prev => ({
                        ...prev,
                        offset: Math.max(0, (prev.offset || 0) - (prev.count || 50)),
                      }))}
                    />
                    <Button style={{ pointerEvents: 'none', minWidth: 44 }}>
                      {Math.floor((queryMode.offset || 0) / (queryMode.count || 50)) + 1}
                    </Button>
                    <Button
                      icon={<RightOutlined />}
                      onClick={() => setQueryMode(prev => ({
                        ...prev,
                        offset: (prev.offset || 0) + (prev.count || 50),
                      }))}
                    />
                  </Button.Group>
                </div>
              </>
            )}
            <div className="variable-field">
              <Tooltip title="Required for stable paging. Defaults to ORDER BY column #1 if unset.">
                <span className="variable-field-label">Order by</span>
              </Tooltip>
              <Select
                size="small"
                allowClear
                showSearch
                placeholder="(column 1)"
                value={queryMode.orderBy || undefined}
                onChange={val => setQueryMode(prev => ({ ...prev, orderBy: val || null, offset: 0 }))}
                style={{ width: 160 }}
                options={(rootColumns || []).map(c => ({ value: c.COLUMN_NAME, label: c.COLUMN_NAME }))}
              />
              <Select
                size="small"
                value={queryMode.orderDir || 'ASC'}
                onChange={val => setQueryMode(prev => ({ ...prev, orderDir: val, offset: 0 }))}
                style={{ width: 80, marginLeft: 4 }}
                options={[
                  { value: 'ASC', label: 'ASC' },
                  { value: 'DESC', label: 'DESC' },
                ]}
              />
            </div>
            <Button size="small" icon={<ClearOutlined />} onClick={() => { setFilters([]); setSelectedFields({}); setExpandedRelations({}); setQueryMode(prev => ({ ...prev, offset: 0, orderBy: null, orderDir: 'ASC' })); }}>
              Reset Query
            </Button>
          </div>

          <div className="query-content">
            {!selectedTable ? (
              <Empty description="Select a table from the left panel to start building a query" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <div className="query-tree">
                <div className="tree-line">
                  <span className="tree-bracket">{'{'}</span>
                </div>

                {/* Root table header */}
                <div className="tree-line">
                  <span className="tree-indent" />
                  <span className="tree-table-header">{rootView.label}</span>
                  {(queryMode.type !== 'all' || queryMode.offset > 0 || queryMode.orderBy) && (
                    <span className="tree-args">
                      ({[
                        queryMode.type !== 'all' ? `${queryMode.type}: ${queryMode.count}` : null,
                        queryMode.offset > 0 ? `offset: ${queryMode.offset}` : null,
                        queryMode.orderBy ? `order_by: ${queryMode.orderBy}` : null,
                        queryMode.orderBy ? `order_dir: ${(queryMode.orderDir || 'ASC').toUpperCase()}` : null,
                      ].filter(Boolean).join(', ')})
                    </span>
                  )}
                  <span className="tree-bracket">{' {'}</span>
                  <span className="tree-collapse-toggle"
                    style={{ fontSize: 10, marginLeft: 'auto', color: '#8250df', cursor: 'pointer', paddingRight: 8 }}
                    onClick={() => setCollapsedPaths(prev => ({ ...prev, [rootPath]: !prev[rootPath] }))}
                  >
                    {collapsedPaths[rootPath] ? <RightOutlined /> : <DownOutlined />}
                  </span>
                </div>

                <div className="tree-line">
                  <span className="tree-indent" />
                  <span className="tree-indent" />
                  <Button
                    type="link"
                    size="small"
                    style={{ fontSize: 11, padding: '0 4px' }}
                    onClick={() => toggleAllFields(rootPath, selectedTable)}
                  >
                    {(selectedFields[rootPath] || []).length === rootColumns.length && rootColumns.length > 0 ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>

                {/* Recursive column tree (columns + inline FK relations) */}
                <div className="query-tree-group" style={{ '--tree-line-left': '48px' }}>
                <TableTreeNode
                  schema={schema}
                  nodeId={selectedTable}
                  path={rootPath}
                  depth={0}
                  columnsCache={columnsCache}
                  loadingColumns={loadingColumns}
                  loadColumns={loadColumns}
                  selectedFields={selectedFields}
                  toggleField={toggleField}
                  toggleAllFields={toggleAllFields}
                  expandedRelations={expandedRelations}
                  toggleRelation={toggleRelation}
                  filters={filters}
                  addFilter={addFilter}
                  removeFilter={removeFilter}
                  updateFilter={updateFilter}
                  collapsedPaths={collapsedPaths}
                  setCollapsedPaths={setCollapsedPaths}
                />
                </div>

                <div className="tree-line">
                  <span className="tree-indent" />
                  <span className="tree-bracket">{'}'}</span>
                </div>
                <div className="tree-line">
                  <span className="tree-bracket">{'}'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Query Preview — collapsible JSON / SQL mode */}
          {(graphqlQuery || generatedSQL) && (
            <div className={`query-preview-section ${previewExpanded ? 'expanded' : 'collapsed'}`}>
              <div className="preview-toolbar" onClick={() => setPreviewExpanded(e => !e)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 10, color: '#8892a4', transition: 'transform 0.2s', transform: previewExpanded ? 'rotate(90deg)' : 'rotate(0)' }}>
                    <RightOutlined />
                  </span>
                  <span style={{ fontWeight: 600, fontSize: 12, color: '#8892a4', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Interpreted Query</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
                  <div className="preview-tabs">
                    <button
                      className={`preview-tab ${previewMode === 'json' ? 'active' : ''}`}
                      onClick={() => setPreviewMode('json')}
                    >GraphQL</button>
                    <button
                      className={`preview-tab ${previewMode === 'sql' ? 'active' : ''}`}
                      onClick={() => setPreviewMode('sql')}
                    >SQL</button>
                  </div>
                  <div className="preview-actions">
                    {previewMode === 'json' ? (
                      <Tooltip title="Copy GraphQL query">
                        <Button size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(graphqlQuery); message.success('GraphQL query copied'); }} disabled={!graphqlQuery}>Copy</Button>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Copy SQL">
                        <Button size="small" icon={<CopyOutlined />} onClick={handleCopySQL} disabled={!generatedSQL}>Copy</Button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
              {previewExpanded && (
                <div className="preview-body">
                  <pre>{previewMode === 'json' ? graphqlQuery : generatedSQL}</pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ════════ RIGHT: Response Panel ════════ */}
        <div className="response-panel">
          <div className="panel-toolbar">
            <span className="toolbar-title"><ThunderboltOutlined /> Response</span>
            {response && !response.error && (
              <Tooltip title="Copy response JSON">
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(response.data, null, 2));
                    message.success('Response JSON copied');
                  }}
                >
                  Copy
                </Button>
              </Tooltip>
            )}
          </div>
          <div className="response-content">
            {executing ? (
              <div className="loading-overlay"><Spin tip="Executing query..." /></div>
            ) : response ? (
              response.error ? (
                <div>
                  <Tag color="red">Error</Tag>
                  <pre className="response-json" style={{ color: '#cf222e', marginTop: 8 }}>
                    {response.error}
                  </pre>
                </div>
              ) : (
                <div className="json-tree-wrapper">
                  <JsonTreeViewer data={response.data} defaultExpanded={3} />
                </div>
              )
            ) : (
              <div className="response-empty">
                <PlayCircleOutlined />
                <span>Click <b>Run</b> to execute the query</span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Results will appear here in JSON format
                </Text>
              </div>
            )}
          </div>
          {response && !response.error && (
            <div className="response-meta">
              <span>Rows: <b>{response.meta?.row_count ?? '—'}</b></span>
              {typeof response.meta?.offset === 'number' && (
                <span>Offset: <b>{response.meta.offset}</b></span>
              )}
              {typeof response.meta?.limit === 'number' && (
                <span>Limit: <b>{response.meta.limit}</b></span>
              )}
              {response.meta?.has_more && (
                <Tooltip title={`Click to load the next page (offset ${response.meta.next_offset})`}>
                  <Button
                    size="small"
                    type="link"
                    icon={<RightOutlined />}
                    onClick={() => {
                      setQueryMode(prev => ({ ...prev, offset: response.meta.next_offset || 0 }));
                      setTimeout(() => handleExecute(), 0);
                    }}
                  >
                    Next page
                  </Button>
                </Tooltip>
              )}
              {response.meta?.truncated && <Tag color="orange">Truncated</Tag>}
              {response.meta?.limit === response.meta?.max_limit && (
                <Tag color="blue">At max page size ({response.meta.max_limit})</Tag>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
